// ./src/utils/api/LookupDictionaryService.ts
import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { User } from "./UserService";
import { LookupDictionaryItem } from "./LookupDictionaryItemService";

// GraphQL queries
const LOOKUP_DICTIONARY_LIST_QUERY = gql`
  query LookupDictionaryList(
    $filter: LookupDictionaryFilter
    $params: PaginationAndSorting
  ) {
    lookupDictionaryList(filter: $filter, params: $params) {
      code
      data {
        id
        accountId
        name
        description
        connectorId
        status
        rights
        version
        createdBy
        updatedBy
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const LOOKUP_DICTIONARY_GET_QUERY = gql`
  query LookupDictionaryGet($id: ID) {
    lookupDictionaryGet(id: $id) {
      code
      data {
        id
        accountId
        name
        description
        connectorId
        status
        rights
        version
        createdBy
        updatedBy
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const LOOKUP_DICTIONARY_GET_MANY_QUERY = gql`
  query LookupDictionaryGetMany($ids: [ID]) {
    lookupDictionaryGetMany(ids: $ids) {
      code
      data {
        id
        accountId
        name
        description
        connectorId
        status
        rights
        version
        createdBy
        updatedBy
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// GraphQL mutations
const LOOKUP_DICTIONARY_CREATE_MUTATION = gql`
  mutation LookupDictionaryCreate($params: LookupDictionaryInput) {
    lookupDictionaryCreate(params: $params) {
      code
      data {
        id
        accountId
        name
        description
        connectorId
        status
        rights
        version
        createdBy
        updatedBy
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const LOOKUP_DICTIONARY_UPDATE_MUTATION = gql`
  mutation LookupDictionaryUpdate(
    $where: LookupDictionaryWhereInput
    $params: LookupDictionaryInput
  ) {
    lookupDictionaryUpdate(where: $where, params: $params) {
      code
      data {
        id
        accountId
        name
        description
        connectorId
        status
        rights
        version
        createdBy
        updatedBy
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const LOOKUP_DICTIONARY_REMOVE_MUTATION = gql`
  mutation LookupDictionaryRemove($where: LookupDictionaryWhereInput) {
    lookupDictionaryRemove(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const LOOKUP_DICTIONARY_REMOVE_MANY_MUTATION = gql`
  mutation LookupDictionaryRemoveMany($where: [LookupDictionaryWhereInput]) {
    lookupDictionaryRemoveMany(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// Types
export interface LookupDictionary {
  id?: string | null | undefined;
  accountId?: string | null | undefined;
  name?: string | null | undefined;
  description?: string | null | undefined;
  connectorId?: string | null | undefined;
  status?: number | null | undefined;
  version?: number | null | undefined;
  createdBy?: string | null | undefined;
  updatedBy?: string | null | undefined;
  createdAt?: string | null | undefined;
  updatedAt?: string | null | undefined;
  userCreated?: User;
  userUpdated?: User;
  lookupDictionaryItems?: LookupDictionaryItem[];
  rights?: string[];
}

export interface LookupDictionaryInput {
  id?: string | null | undefined;
  accountId?: string | null | undefined;
  name?: string | null | undefined;
  description?: string | null | undefined;
  connectorId?: string | null | undefined;
  status?: number | null | undefined;
}

export interface LookupDictionaryFilter {
  id?: string[] | null | undefined;
  accountId?: string[] | null | undefined;
  status?: number[] | null | undefined;
  searchKeyword?: string | null | undefined;
}

export interface LookupDictionaryWhereInput {
  id?: string | undefined;
}

/**
 * LookupDictionary service that handles all lookup dictionary-related API calls
 * Supports full CRUD operations: list, get, getMany, create, update, remove, removeMany
 */
export class LookupDictionaryService {
  /**
   * Get a list of lookup dictionaries with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: LookupDictionaryFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(LOOKUP_DICTIONARY_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Get a single lookup dictionary by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(LOOKUP_DICTIONARY_GET_QUERY, {
      id: id,
    });
    return result;
  }

  /**
   * Get multiple lookup dictionaries by IDs (preserves order)
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(
      LOOKUP_DICTIONARY_GET_MANY_QUERY,
      {
        ids: ids,
      }
    );
    return result;
  }

  /**
   * Create a new lookup dictionary
   */
  static async create(params: LookupDictionaryInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      LOOKUP_DICTIONARY_CREATE_MUTATION,
      {
        params,
      }
    );
    return result;
  }

  /**
   * Update an existing lookup dictionary
   */
  static async update(
    where: LookupDictionaryWhereInput,
    params: LookupDictionaryInput
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      LOOKUP_DICTIONARY_UPDATE_MUTATION,
      {
        where,
        params,
      }
    );
    return result;
  }

  /**
   * Remove/delete a lookup dictionary
   */
  static async remove(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      LOOKUP_DICTIONARY_REMOVE_MUTATION,
      {
        where: { id },
      }
    );
    return result;
  }

  /**
   * Remove/delete multiple lookup dictionaries
   */
  static async removeMany(ids: string[]): Promise<OpResult> {
    const where = ids.map((id) => ({ id }));
    const result = await ApiService.executeMutation(
      LOOKUP_DICTIONARY_REMOVE_MANY_MUTATION,
      {
        where,
      }
    );
    return result;
  }
}
